import type {
  CreatePlanBody,
  PlanDto,
  TargetedPlanDto,
  UpdatePlanBody,
} from '@planuze/admin-contracts';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  archivePlan,
  bulkArchivePlans,
  createPlan,
  listPlans,
  listTargetedPlans,
  updatePlan,
} from '../lib/plans-api.js';

const PUBLIC_KEY = ['plans', 'public'] as const;
const TARGETED_KEY = ['plans', 'targeted'] as const;

/* Hooks TanStack Query do domínio planos (ADR-0419/0435). As duas listas (pública e negociada)
   são paginadas por cursor keyset (`useInfiniteQuery`); cada aba tem sua própria paginação. */

type PublicPage = Awaited<ReturnType<typeof listPlans>>;
type TargetedPage = Awaited<ReturnType<typeof listTargetedPlans>>;
type PublicInfinite = InfiniteData<PublicPage>;
type TargetedInfinite = InfiniteData<TargetedPage>;

/* `enabled` habilita a busca lazy por aba (ADR-0704): a lista só é buscada quando a aba
   está ativa. Default `true` p/ os call sites que sempre querem os dados. */
export function usePlansList(enabled = true) {
  return useInfiniteQuery({
    queryKey: PUBLIC_KEY,
    queryFn: ({ pageParam }) => listPlans({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

/* Planos DIRECIONADOS (negociados) — list paginada (ADR-0435). Criar reusa `useCreatePlan`
   com alvo/validade no body. */
export function useTargetedPlansList(enabled = true) {
  return useInfiniteQuery({
    queryKey: TARGETED_KEY,
    queryFn: ({ pageParam }) => listTargetedPlans({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

/* Invalida ambas as listas — criar/arquivar um plano direcionado também afeta a aba
   Negociados, e o público pode mudar junto. */
function useInvalidateAll(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PUBLIC_KEY }),
      queryClient.invalidateQueries({ queryKey: TARGETED_KEY }),
    ]);
  };
}

export function useCreatePlan() {
  /* createPlan cria plano público OU direcionado (com alvo) → invalida as duas listas. */
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: CreatePlanBody) => createPlan(body),
    onSuccess: invalidate,
  });
}

export function useUpdatePlan() {
  /* O mesmo PATCH edita público ou negociado (ADR-0888). A reconciliação também ocorre no erro:
     em especial, um CAS 409 precisa trazer o `updatedAt` novo antes de o operador reabrir o modal. */
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: { readonly id: string; readonly body: UpdatePlanBody }) =>
      updatePlan(input.id, input.body),
    onSettled: invalidate,
  });
}

/* Remove o plano `id` de todas as páginas carregadas de uma lista infinita (arquivar otimista). */
function dropFromPages<
  T extends { readonly id: string },
  P extends { readonly items: readonly T[] },
>(data: InfiniteData<P> | undefined, id: string): InfiniteData<P> | undefined {
  if (data === undefined) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, items: page.items.filter((it) => it.id !== id) })),
  };
}

/* Arquivar OTIMISTA (ADR-0699): remove o plano das duas listas (todas as páginas carregadas) no
   mesmo frame, reconcilia no onSettled e faz rollback no erro — sem esperar o refetch. */
export function useArchivePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archivePlan(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ['plans'] });
      const prevPublic = queryClient.getQueryData<PublicInfinite>(PUBLIC_KEY);
      const prevTargeted = queryClient.getQueryData<TargetedInfinite>(TARGETED_KEY);
      queryClient.setQueryData<PublicInfinite>(PUBLIC_KEY, (old) =>
        dropFromPages<PlanDto, PublicPage>(old, id),
      );
      queryClient.setQueryData<TargetedInfinite>(TARGETED_KEY, (old) =>
        dropFromPages<TargetedPlanDto, TargetedPage>(old, id),
      );
      return { prevPublic, prevTargeted };
    },
    onError: (_err, _id, context) => {
      if (context?.prevPublic !== undefined) {
        queryClient.setQueryData(PUBLIC_KEY, context.prevPublic);
      }
      if (context?.prevTargeted !== undefined) {
        queryClient.setQueryData(TARGETED_KEY, context.prevTargeted);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PUBLIC_KEY });
      queryClient.invalidateQueries({ queryKey: TARGETED_KEY });
    },
  });
}

export function useBulkArchivePlans() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (ids: readonly string[]) => bulkArchivePlans(ids),
    onSuccess: invalidate,
  });
}
